import { get } from 'lodash';
import {
  Events as RetentionEvents,
  type EventTransportConfig,
  type EventTransportEndpoints,
} from '@bbk/retention-sdk/events';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

export {
  EventSource,
  navigateInThisWindow,
  type EventObject,
  type EventSyncObject,
  type EventTransportConfig,
  type EventTransportEndpoints,
  type ExecuteEventsHandler,
  type FunnelNavigate,
  type NextStepHandler,
  type PricingPageEventProperties,
  type Resolvable,
  type SessionKeyResolver,
  type SubmissionConfig,
} from '@bbk/retention-sdk/events';

/**
 * A merchant preview (`/preview-experience`) or an `isDryRun=true` run. Neither
 * has a cancel session, so nothing may be delivered or created server-side.
 */
export const isDryRunPage = (): boolean =>
  /isDryRun=true/.test(window.location.search) ||
  /preview-experience/.test(window.location.pathname);

/**
 * Compatibility adapter for the hosted funnel.
 *
 * The SDK takes its endpoints, token, session key and dry-run flag as explicit
 * inputs. This supplies them from the same implicit sources the funnel has
 * always used, so the emitted requests are unchanged.
 *
 * Every value is read per request rather than captured once, which is required
 * rather than incidental: `Funnel.tsx` and `Complete.tsx` construct this at
 * module scope, before the loader has fetched the experience and assigned the
 * globals, and the token is replaced every 24 hours because it expires after
 * two days.
 */
export const windowDerivedConfig: EventTransportConfig = {
  endpoints: (): EventTransportEndpoints => ({
    event: window.bbkClientConfig?.event_api_endpoint,
    sync: window.bbkClientConfig?.sync_api_endpoint,
    eventLogs: window.bbkClientConfig?.event_log_api_endpoint,
    syncLogs: window.bbkClientConfig?.sync_log_api_endpoint,
  }),

  authenticityToken: () => window.bbkAuthenticityToken || '',

  /**
   * Path index 2 holds the key for `/session/{key}`. On a lifecycle path the
   * key is taken from the payload instead, because the embed is not served
   * under a session URL.
   *
   * The index is positional and therefore wrong for `/api/v1/session/{key}`,
   * where the key sits at index 3. Preserved as-is; an SDK caller passes the
   * key explicitly and avoids the question.
   */
  sessionKey: (session) => {
    const path = window.location.pathname;
    const fromPath = get(path.split('/'), 2);
    return path.includes('lifecycle') ? session : fromPath;
  },

  dryRun: isDryRunPage,

  debug: () => window.bbkClientConfig?.debug ?? false,
  diagnostics: GlobalLogger,
};

/**
 * Pre-wired so the existing `new Events()` call sites need no change.
 */
export class Events extends RetentionEvents {
  constructor(config: EventTransportConfig = windowDerivedConfig) {
    super({
      ...config,
      diagnostics: config.diagnostics ?? windowDerivedConfig.diagnostics,
    });
  }
}
